import { faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LineBatchMeta } from '@shared/components/ui/LineBatchMeta';
import { Product } from '@shared/components/ui/Product';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { useToast } from '@shared/hooks/use-toast';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Tooltip } from '@shared/components/ui/tooltip';
import { Badge } from '@shared/components/ui/shadcn/badge';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { apiClientWH } from '@shared/services/ApiClient';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Clock, MapPin, Package, User, XCircle, Image as ImageIcon } from 'lucide-react';
import { createPortal } from 'react-dom';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReceiptApiAdapter } from '../../adapters/ReceiptApiAdapter';
import { EntryReceiptMovement } from '../../../models/Entry';

export interface MovementsModalRef {
  open: (movements: EntryReceiptMovement[]) => void;
}

interface MovementsModalProps {
  entryId: number;
  receiptId: number;
  onMovementDeleted?: () => Promise<void> | void;
}

const buildImageUrl = (imageUrl?: string | null): string => {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('http')) return imageUrl;
  return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageUrl}`;
};

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '-';
  const isoDateString = dateString.replace(' ', 'T');
  const date = new Date(isoDateString);
  if (isNaN(date.getTime())) return dateString;

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const MovementsModal = forwardRef<MovementsModalRef, MovementsModalProps>(
  ({ entryId, receiptId, onMovementDeleted }, ref) => {
    const { t } = useTranslation('merchandiseV3');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const [movements, setMovements] = useState<EntryReceiptMovement[]>([]);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const receiptAdapterRef = useRef(new ReceiptApiAdapter(createHttpClient(apiClientWH)));

    const deleteMovementMutation = useMutation({
      mutationFn: async (movement: EntryReceiptMovement) => {
        await receiptAdapterRef.current.deleteMovement(entryId, receiptId, movement.id);
        return movement;
      },
      onSuccess: async (movement) => {
        setMovements((prev) => prev.filter((item) => item.id !== movement.id));
        toast({
          title: t('movements_modal.delete_success_title', 'Movimiento eliminado'),
          description: t('movements_modal.delete_success_description', 'El movimiento se eliminó correctamente'),
        });
        await onMovementDeleted?.();
      },
      onError: (error: any) => {
        const apiMessage = error?.response?.data?.message;
        toast({
          title: t('movements_modal.delete_error_title', 'No se pudo eliminar el movimiento'),
          description:
            apiMessage ||
            error?.message ||
            t('movements_modal.delete_error_description', 'Ocurrió un error inesperado al eliminar el movimiento'),
          variant: 'destructive',
        });
      },
    });

    useImperativeHandle(ref, () => ({
      open: (movementsData: EntryReceiptMovement[]) => {
        setMovements(movementsData);
        setSelectedImage(null);
        modalRef.current?.openModal();
      },
    }));

    // Cerrar imagen con Escape
    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && selectedImage) {
          setSelectedImage(null);
        }
      };

      if (selectedImage) {
        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden';
      }

      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }, [selectedImage]);

    // Ordenar por fecha de creación (más recientes primero)
    const sortedMovements = [...movements].sort((a, b) => {
      if (!a.created_at) return 1;
      if (!b.created_at) return -1;
      try {
        const dateA = new Date(a.created_at.replace(' ', 'T'));
        const dateB = new Date(b.created_at.replace(' ', 'T'));
        if (isNaN(dateA.getTime())) return 1;
        if (isNaN(dateB.getTime())) return -1;
        return dateB.getTime() - dateA.getTime();
      } catch {
        return 0;
      }
    });

    return (
      <ModalCenter
        ref={modalRef}
        title={t('movements_modal.title', 'Movimientos Registrados')}
        type="info"
        width="xxl"
        classNameBody="max-h-[calc(100vh-8rem)]"
      >
        <div className="space-y-4">
          {movements.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">{t('movements_modal.empty', 'No hay movimientos registrados')}</p>
            </div>
          ) : (
            <>
              {/* Encabezados */}
              <div className="grid grid-cols-[2fr_auto_auto_1.5fr_2fr_auto_2fr_1.5fr_auto] gap-3 px-4 py-3 bg-muted/70 rounded-lg text-sm text-foreground">
                <div>{t('movements_modal.product', 'Producto')}</div>
                <div className="text-center">{t('movements_modal.type', 'Tipo')}</div>
                <div className="text-center">{t('movements_modal.quantity', 'Cantidad')}</div>
                <div>{t('movements_modal.location', 'Ubicación')}</div>
                <div>{t('movements_modal.user_date', 'Usuario / Fecha')}</div>
                <div className="text-center">{t('movements_modal.status', 'Estado')}</div>
                <div>{t('movements_modal.rejection_reason', 'Motivo del rechazo')}</div>
                <div>{t('movements_modal.rejection_evidence', 'Evidencias')}</div>
                <div className="text-center">{t('movements_modal.actions', 'Acciones')}</div>
              </div>

              {/* Lista - altura acotada al viewport para no desbordar la pantalla */}
              <ScrollArea className="h-[min(500px,calc(100vh-14rem))]">
                {/* Lista de movimientos - Movements list (E2E) */}
                <div className="space-y-2 pr-4" data-testid="modal-movimientos-registrados-content">
                  {sortedMovements.map((movement) => {
                    const isAccepted = movement.movement_type === 'ACCEPTED';
                    const productName = movement.product?.name || `Producto ${movement.product_id}`;
                    const productCode = movement.product?.code || '';
                    const productImage = movement.product?.img || null;
                    const locationCode = movement.location?.code || `#${movement.location_id}`;
                    const userName = movement.user?.name || t('receive.movements.unknown_user', 'Usuario desconocido');

                    return (
                      <div
                        key={movement.id}
                        className={`grid grid-cols-[2fr_auto_auto_1.5fr_2fr_auto_2fr_1.5fr_auto] gap-3 px-4 py-4 border rounded-lg transition-colors ${
                          isAccepted
                            ? 'bg-background border-border hover:border-green-300 dark:hover:border-green-700'
                            : 'bg-background border-border hover:border-red-300 dark:hover:border-red-700'
                        }`}
                      >
                        {/* Producto */}
                        <div className="flex items-center min-w-0">
                          <div className="min-w-0">
                            <Product
                              id={movement.product_id}
                              name={productName}
                              src={buildImageUrl(productImage)}
                              code={productCode}
                              showImageModal={true}
                              enableLineClamp={true}
                            />
                            {isAccepted && (movement.batch?.batch_number || movement.batch_id != null) && (
                              <LineBatchMeta
                                batch={movement.batch}
                                batchId={movement.batch_id}
                                i18nNs="merchandiseV3"
                                lotLabelKey="receive.movements.line_batch_label"
                                expiryLabelKey="receive.movements.line_expiry_label"
                                className="mt-1"
                              />
                            )}
                          </div>
                        </div>

                        {/* Tipo */}
                        <div className="flex items-center justify-center">
                          {isAccepted ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                          )}
                        </div>

                        {/* Cantidad */}
                        <div className="flex items-center justify-center">
                          <span className="text-sm font-medium text-foreground">{movement.qty}</span>
                        </div>

                        {/* Ubicación - Location (truncado + tooltip si el código es largo) */}
                        <div className="flex min-w-0 items-center overflow-hidden pl-3">
                          {isAccepted ? (
                            <div className="flex min-w-0 max-w-full flex-1 items-center gap-2">
                              <MapPin className="h-4 w-4 flex-shrink-0 text-primary" />
                              <Tooltip
                                content={<span className="max-w-sm break-words text-sm text-text">{locationCode}</span>}
                                trigger={
                                  <span className="max-w-[min(100%,12rem)] min-w-0 flex-1 cursor-default truncate text-left text-sm font-mono text-foreground">
                                    {locationCode}
                                  </span>
                                }
                                side="top"
                                data-testid={`movement-location-tooltip-${movement.id}`}
                              />
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </div>

                        {/* Usuario / Fecha */}
                        <div className="flex flex-col justify-center pl-3 min-w-0">
                          {movement.user ? (
                            <div className="flex items-center gap-2 mb-1">
                              <User className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              <span className="text-sm text-foreground truncate">{userName}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm mb-1">-</span>
                          )}
                          <div className="flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                            <span className="text-xs text-muted-foreground truncate">
                              {formatDate(movement.created_at)}
                            </span>
                          </div>
                        </div>

                        {/* Estado */}
                        <div className="flex items-center justify-center">
                          {isAccepted ? (
                            movement.dispatch_status === 'DISPATCHED' ? (
                              <Badge variant="default" className="text-xs">
                                {t('receive.movements.dispatched', 'Despachado')}
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">
                                {t('receive.movements.pending', 'Pendiente')}
                              </Badge>
                            )
                          ) : movement.rejection_type ? (
                            <Badge variant="destructive" className="text-xs">
                              {movement.rejection_type}
                            </Badge>
                          ) : (
                            <Badge variant="destructive" className="text-xs">
                              {t('receive.movements.rejected', 'Rechazado')}
                            </Badge>
                          )}
                        </div>

                        {/* Motivo del rechazo - Columna */}
                        <div className="flex items-center pl-3 min-w-0">
                          {!isAccepted && movement.rejection_text ? (
                            <span className="text-sm text-foreground line-clamp-2" title={movement.rejection_text}>
                              {movement.rejection_text}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </div>

                        {/* Evidencias - Columna */}
                        <div className="flex items-center gap-1.5 pl-3 flex-wrap">
                          {!isAccepted && movement.attachments && movement.attachments.length > 0 ? (
                            movement.attachments.map((attachmentUrl, index) => {
                              const fullImageUrl = buildImageUrl(attachmentUrl);
                              return (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => setSelectedImage(fullImageUrl)}
                                  className="relative group overflow-hidden rounded border border-border hover:border-primary transition-colors flex-shrink-0"
                                  data-testid={`rejection-image-${movement.id}-${index}`}
                                >
                                  <img
                                    src={fullImageUrl}
                                    alt={t('movements_modal.rejection_evidence_image', `Evidencia ${index + 1}`)}
                                    className="w-12 h-12 object-cover group-hover:opacity-80 transition-opacity"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                    }}
                                  />
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                    <ImageIcon className="w-3 h-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </div>
                                </button>
                              );
                            })
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </div>

                        {/* Acciones */}
                        <div className="flex items-center justify-center">
                          {isAccepted && movement.dispatch_status !== 'DISPATCHED' && movement.location_id > 0 ? (
                            <button
                              type="button"
                              onClick={() => deleteMovementMutation.mutate(movement)}
                              disabled={deleteMovementMutation.isPending}
                              className="w-8 h-8 rounded-md text-error hover:bg-bg-error disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                              data-testid={`delete-movement-button-${movement.id}`}
                              aria-label={t('movements_modal.delete', 'Eliminar')}
                              title={t('movements_modal.delete', 'Eliminar')}
                            >
                              <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </>
          )}
        </div>

        {/* Modal para ver imagen de rechazo en grande */}
        {selectedImage && (
          <>
            {createPortal(
              <div
                className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80"
                onClick={() => setSelectedImage(null)}
                data-testid="rejection-image-modal-overlay"
              >
                <div
                  className="relative max-w-[95vw] max-h-[95vh] flex flex-col animate-in zoom-in-95 duration-300"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header del modal */}
                  <div className="bg-body-bg border-card-border border p-4 rounded-t-2xl shadow-2xl flex justify-between items-center">
                    <span className="text-sm font-medium text-title">
                      {t('movements_modal.rejection_evidence_view', 'Evidencia de rechazo')}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedImage(null)}
                      className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-hover-press text-icons hover:text-text transition-all"
                      data-testid="rejection-image-modal-close"
                      aria-label={t('movements_modal.close_image', 'Cerrar imagen')}
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Imagen */}
                  <div className="relative bg-body-bg rounded-b-2xl border border-card-border border-t-0 overflow-hidden flex items-center justify-center min-h-[200px]">
                    <img
                      src={selectedImage}
                      alt={t('movements_modal.rejection_evidence', 'Evidencia de rechazo')}
                      className="block object-contain w-auto h-auto max-h-[calc(90vh-80px)]"
                      data-testid="rejection-image-modal-image"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const errorDiv = document.createElement('div');
                        errorDiv.className = 'text-text text-center p-4';
                        errorDiv.textContent = t('movements_modal.image_load_error', 'Error al cargar la imagen');
                        target.parentElement?.appendChild(errorDiv);
                      }}
                    />
                  </div>
                </div>
              </div>,
              document.body,
            )}
          </>
        )}
      </ModalCenter>
    );
  },
);

MovementsModal.displayName = 'MovementsModal';
